import * as deepmerge from 'deepmerge'
import { mapMutations, mapState, mapGetters } from 'vuex';
import productCategories from '@js/v2/src/config/products/product-categories';

export default {
    data() {
        return {
            gotData: false,
            showBandwidth: true,
            showDaily: true,
            // This checked value causes an error to appear everywhere b-form-checkbox component is used
            // Apparently there is a conflict with a named prop in those components
            // Should remove this if possible, or rename to remove conflicts (not sure where referenced)
            checked: false,
            isUserLoggedIn: false,
        }
    },
    computed: {
        // Vuex Mappings - available in ./store.js
        ...mapState(['userDetails', 'accountHolder', 'userFeatures', 'creditCardImage', 'paymentMethodSelected', 'clientHasActivePaypalBA', 'clientActivePaypalBAEmail',
            'remoteManagement', 'userHasVerified', 'displayProfileUpdateForm', 'isEditingAccountHolderProfile', 'currentService', 'profileWidget', 'environment', 'paymentView', 'bpayBillerCode',
            'invoice', 'activeSidebar', 'serviceSupportAction', 'icons', 'off', 'on', 'actions', 'messages', 'overdueServices', 'productCategories'
        ]),

        ...mapGetters([
            'guestAccessEnabled',
            'hasRemoteManagement',
            'ccLastFour'
        ]),
        // Dynamic link to the network component for each service type
        networkLink() {
            return '/service/' + this.getServiceType() + '/' + this.getServiceId() + '/network';
        },
        // Dynamic link tp the access component for each service type
        requestAccessLink() {
            return '/service/' + this.getServiceType() + '/' + this.getServiceId() + '/access';
        },
        domainDetailsLink() {
            return '/service/domains/' + this.getServiceId() + '/details';
        },
        domainNameserversLink() {
            return '/service/domains/' + this.getServiceId() + '/nameservers';
        },
        domainContactsLink() {
            return '/service/domains/' + this.getServiceId() + '/contacts';
        },
        canUpdateDomain() {
            return this.domain.status.toLowerCase() == 'active' ? true : false;
        },
        userCurrency() {
            return this.userDetails.billing.currency;
        },
        getUsersAppliedTaxName() {
            return this.userDetails.billing.tax && this.userDetails.billing.tax.name ? this.userDetails.billing.tax.name : null;
        },
        getUsersAppliedTaxRate() {
            return this.userDetails.billing.tax && this.userDetails.billing.tax.rate ? this.userDetails.billing.tax.rate : null;
        },
    },
    methods: {
        /**
         * vuexLoading ()
         *  Api calls require user apiToken from Vuex - ensure Vuex is populated before making api calls (flag window.vuexLoading)
         * @return {Promise} - flags that vuex now contains data
         */
        vuexLoading() {
            return new Promise(function(resolve, reject) {
                /** Set timeout to wait for window.vuexLoading */
                let check = setInterval(() => {
                    if(!window.vuexLoading) {
                        clearInterval(check);
                        resolve(true);
                    }
                }, 100);
            });
        },
        /**
         * @param {int} id
         * @return {string}
         */
        downloadInvoiceLink(id) {
            return '/billing/invoices/' + id + '/download';
        },
        /**
         * @param {int} id
         * @return {string}
         */
        downloadDocumentLink(id) {
            return '/account/documents/' + id + '/download';
        },
        /**
         *  Get service id from url - can't pass down props when using sau-page-nav
         * @return {integer}
         */
        getServiceId() {
            let getServiceId = new RegExp('\(\/[0-9]+\/\)');
            let serviceId = getServiceId.exec(window.location.pathname);
            /** Replace the / and return the first index */
            return serviceId[0].replace(new RegExp('[\/]', 'g'), '');
        },
        /**
         *  Get serviceType from url - can't pass down props when using sau-page-nav
         * @return {string}
         */
        getServiceType() {
            /** match everything after /service/ and before the next / */
            let getServiceType = new RegExp('\/service\/([A-Za-z-]+)');
            let serviceType = getServiceType.exec(window.location.pathname);
            /** Return the first index */
            return serviceType[1];
        },
        /**
         *  Get two factor method from url
         * @return {string}
         */
        getTwoFactorMethod() {
            let getTwoFactorType = new RegExp('\/two-factor\/([A-Za-z]+)');
            let twoFactor = getTwoFactorType.exec(window.location.pathname);
            /** Return the first index */
            return twoFactor[1];
        },
        /**
         *  Get the current page - used by the support sidepanel to determine where the request came from
         * @return array
         */
        getCurrentPage() {
            let url = [];
            /** Filter out empty values */
            window.location.pathname.split('/').forEach(function (value, index) {
                if (value) {
                    url.push(value);
                }
            });
            return url;
        },
        /**
         *  open sidepanel support form
         * @return void
         */
        actionSupport() {
            window.location.href = '/support';
        },
        /**
         * @param {date|string} date
         * @return {date}
         */
        convertDate(date) {
            return Date.parse(date);
        },
        /** @return void */
        modalHide(modal) {
            return this.$refs[modal].hide();
        },
        /**
         * @param {string} number
         * @return {object}
         */
        getCreditCardType(number) {
            /** Mastercard */
            let mc = new RegExp('^5[1-5]');
            if (mc.test(number)) {
                return { type: 'mastercard', image: '/img/cc-mc-aa.gif' };
            }

            /** Visa */
            let visa = new RegExp('^4[0-9]');
            if (visa.test(number)) {
                return { type: 'visa', image: '/img/cc-vs-aa.gif' };
            }

            /** American Express */
            let amex = new RegExp('^3[47]');
            if (amex.test(number)) {
                return { type: 'american express', image: '/img/cc-amex-aa.gif' };
            }

            /** No card matches */
            return { type: 'unknown', image: '/img/cc-blank-aa.gif' };
        },
        /**
         * Only the card type and last 4 digits are returned once saved so we can't match by card number like getCreditCardType() method above
         * @param {string} type
         * @return {string}
         */
        getCreditCardImage() {
            switch (this.userDetails.billing.card_type.toLowerCase()) {
                case 'visa':
                    return '/img/cc-vs-bb.gif';
                case 'mastercard':
                    return '/img/cc-mc-bb.gif';
                case 'american express':
                case 'amex':
                    return '/img/cc-ax-bb.gif';
                default:
                    return '/img/cc-blank-bb.gif';
            }
        },

        /**
         *  helper function to determine if the logged in user has the supplied permission
         * @param {string} permission
         * @return boolean
         */
        hasPermission(permission) {
            // Clients always have permission
            if (!this.userDetails.whmcs_contact_id) {
                return true;
            }

            return this.userDetails.permissions.split(",").indexOf(permission) > -1;
        },
        /**
         *  Used for single column searching for Data Tables
         * @param {string} column
         * @param {string} data
         * @return {string}
         */
        applyFilter(column, data) {
            return column + ":" + data;
        },
        /**
         *  Axios wrapper which returns a promise for get requests
         * @param {string} url
         * @return {Promise}
         */
        getRequest(url) {
            return new Promise(function (resolve, reject) {
                axios.get(url, {
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => {
                        this.gotData = true;
                        resolve(response);
                    })
                    .catch(error => {
                        this.gotData = true;
                        reject(error);
                    });
            }.bind(this));
        },
        /**
         * Axios wrapper which returns a promise for post requests
         * @param {string} url
         * @param {array} data
         */
        postRequest(url, data, options = {}) {
            return new Promise(function (resolve, reject) {
                axios.post(url, data, deepmerge({
                    headers: {
                        'Accept': 'application/json',
                    },
                }, options)).then(response => {
                    this.gotData = true;
                    resolve(response);
                }).catch(error => {
                    this.gotData = true;
                    reject(error);
                });
            }.bind(this));
        },
        /**
         * Axios wrapper which returns a promise for put requests
         * @param {string} url
         * @param {array} data
         */
        putRequest(url, data) {
            return new Promise(function (resolve, reject) {
                axios.put(url, data, {
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => {
                        this.gotData = true;
                        resolve(response);
                    })
                    .catch(error => {
                        this.gotData = true;
                        reject(error);
                    })
            }.bind(this));
        },
        /**
         * Axios wrapper which returns a promise for delete requests
         * @param {string} url
         */
        deleteRequest(url) {
            return new Promise(function (resolve, reject) {
                axios.delete(url, {
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => {
                        this.gotData = true;
                        resolve(response);
                    })
                    .catch(error => {
                        this.gotData = true;
                        reject(error);
                    })
            }.bind(this));
        },
        /**
         * Axios wrapper which returns a promise for put requests
         * @param {string} url
         * @param {array} data
         */
        patchRequest(url, data) {
            return new Promise(function (resolve, reject) {
                axios.patch(url, data, {
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => {
                        this.gotData = true;
                        resolve(response);
                    })
                    .catch(error => {
                        this.gotData = true;
                        reject(error);
                    })
            }.bind(this));
        },
        /**
         *  Axios wrapper which returns a promise for get requests
         * @param {string} url
         * @return {Promise}
         */
        syncGetRequest(url) {
            return new Promise(function (resolve, reject) {
                window.syncAxios.get(url, {
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => {
                        this.gotData = true;
                        // Errors are thened for some reason.
                        if (response.response) {
                            reject(response);
                            return
                        }
                        resolve(response);
                    })
                    .catch(error => {
                        this.gotData = true;
                        reject(error);
                    });
            }.bind(this));
        },
        /**
         * Axios wrapper which returns a promise for post requests
         * @param {string} url
         * @param {array} data
         */
        syncPostRequest(url, data, options = {}) {
            return new Promise(function (resolve, reject) {
                window.syncAxios.post(url, data, deepmerge({
                    headers: {
                        'Accept': 'application/json',
                    },
                }, options)).then(response => {
                    this.gotData = true;
                    // Errors are thened for some reason.
                    if (response.response) {
                        reject(response);
                        return
                    }
                    resolve(response);
                }).catch(error => {
                    this.gotData = true;
                    reject(error);
                });
            }.bind(this));
        },
        /**
         * Axios wrapper which returns a promise for put requests
         * @param {string} url
         * @param {array} data
         */
        syncPutRequest(url, data) {
            return new Promise(function (resolve, reject) {
                window.syncAxios.put(url, data, {
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => {
                        this.gotData = true;
                        // Errors are thened for some reason.
                        if (response.response) {
                            reject(response);
                            return
                        }
                        resolve(response);
                    })
                    .catch(error => {
                        this.gotData = true;
                        reject(error);
                    })
            }.bind(this));
        },
        /**
         * Axios wrapper which returns a promise for delete requests
         * @param {string} url
         */
        syncDeleteRequest(url) {
            return new Promise(function (resolve, reject) {
                window.syncAxios.delete(url, {
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => {
                        this.gotData = true;
                        // Errors are thened for some reason.
                        if (response.response) {
                            reject(response);
                            return
                        }
                        resolve(response);
                    })
                    .catch(error => {
                        this.gotData = true;
                        reject(error);
                    })
            }.bind(this));
        },
        /**
         * Axios wrapper which returns a promise for put requests
         * @param {string} url
         * @param {array} data
         */
        syncPatchRequest(url, data) {
            return new Promise(function (resolve, reject) {
                window.syncAxios.patch(url, data, {
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => {
                        this.gotData = true;
                        // Errors are thened for some reason.
                        if (response.response) {
                            reject(response);
                            return
                        }
                        resolve(response);
                    })
                    .catch(error => {
                        this.gotData = true;
                        reject(error);
                    })
            }.bind(this));
        },
        /** GET REQUESTS */
        getServices() {
            return this.getRequest('/api/services');
        },
        getServicesSummary() {
            return this.getRequest('/api/services/summary');
        },
        getService() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId());
        },
        getLoginDetails() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/login-details');
        },
        getCreditCard() {
            return this.getRequest('/api/clients/' + this.userDetails.whmcs_client_id + '/credit-card');
        },
        getBpayRef() {
            return this.getRequest('/api/clients/' + this.userDetails.whmcs_client_id + '/bpay');
        },
        checkPendingCancellations() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/check-cancellation');
        },
        getServiceInvoices() {
            return this.getRequest('/api/services/' + this.getServiceId() + '/invoices');
        },
        getAccessDetails() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/access');
        },
        getAddons() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/addons');
        },
        getBandwidthQuota() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/bandwidth/quota');
        },
        getCurrentBandwidthUsage() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/bandwidth/current');
        },
        getCurrentPortData() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/bandwidth/ports');
        },
        getDeviceStatus() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/status');
        },
        getDeviceInfo() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/info')
        },
        requestAccessDetails() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/access/request');
        },
        getHadraasInfo() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/info')
        },
        checkPendingUpgrade() {
            return this.getRequest('/api/services/legacy/' + this.getServiceId() + '/check-upgrade');
        },
        getContacts() {
            return this.getRequest('/api/contacts');
        },
        getAccountHolder() {
            return this.getRequest('/api/clients/' + this.userDetails.whmcs_client_id);
        },
        checkExtensionRequests() {
            return this.getRequest('/api/billing/extensions');
        },
        getCreditBalance() {
            return this.getRequest('/api/billing/credit-balance');
        },
        getInvoices() {
            return this.getRequest('/api/billing/invoices');
        },
        getInvoice(id) {
            return this.getRequest('/api/billing/invoices/' + id);
        },
        getPaypalBAState() {
            return this.getRequest('/api/billing/paypal-ba');
        },
        getApiTokens() {
            return this.getRequest('/api/tokens');
        },
        getPendingCharges() {
            return this.getRequest('/api/billing/pending-charges');
        },
        getUninvoicedItems() {
            return this.getRequest('/api/billing/uninvoiced-items');
        },
        getTransactions() {
            return this.getRequest('/api/billing/transactions');
        },
        getPaymentMethods() {
            return this.getRequest('/api/billing/payment-methods').then(response => {
                const cc = response.data;
                if(cc.card_last_four) {
                    this.$store.commit('setCreditCardLastFour', cc.card_last_four);
                    this.$store.commit('setCreditCardType', cc.card_type);
                    this.$store.commit('setCreditCardExp', cc.card_exp);
                }
                return response.data;
            });
        },
        getVpsUsageCpu() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/cpu');
        },
        getVpsTransactions() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/transactions');
        },
        getPerformanceMetrics() {
            return this.getRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/metrics');
        },
        getConversation(id) {
            return this.getRequest('/api/support/ticket/' + id);
        },
        getConversations() {
            return this.getRequest('/api/support/conversations');
        },
        getClientCustomFields() {
            return this.getRequest('/api/clients/' + this.userDetails.whmcs_client_id + '/custom-fields');
        },
        getDomains() {
            return this.getRequest('/api/domains');
        },
        getDomainDetails() {
            return this.getRequest('/api/domains/' + this.getServiceId());
        },
        getDomainNameservers() {
            return this.getRequest('/api/domains/' + this.getServiceId() + '/nameservers');
        },
        getDomainContacts() {
            return this.getRequest('/api/domains/' + this.getServiceId() + '/contacts');
        },
        checkConversationFeedback(id) {
            return this.getRequest(`/api/support/feedback/conversations/${id}`);
        },
        getAccountDocuments() {
            return this.getRequest('/api/accounts/documents');
        },
        getAccountAuthenticatorDetails() {
            return this.getRequest('/api/accounts/security/authenticator/setup');
        },
        /** POST REQUESTS */
        sendConversationFeedback(id, data) {
            return this.postRequest(`/api/support/feedback/conversations/${id}`, data);
        },
        sendConversationReplyFeedback(id, reply_id, data) {
            return this.postRequest(`/api/support/feedback/conversations/${id}/replies/${reply_id}`, data);
        },
        payInvoice(data, id) {
            return this.postRequest('/api/payment/invoice/' + id, data)
        },
        getReverseDNS(data) {
            return this.postRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/dns', data);
        },
        updateReverseDNS(data) {
            return this.postRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/dns/update', data);
        },
        resetReverseDNS(data) {
            return this.postRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/dns/reset', data);
        },
        rebuildVpsTemplate(data) {
            return this.postRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/rebuild', data);
        },
        updateHostPassword(data) {
            return this.postRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/password/update', data);
        },
        createConversation(data) {
            return this.postRequest('/api/support/conversations', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        resetPassword(data) {
            return this.postRequest('/api/accounts/resetpassword', data);
        },
        submitUpgradeRequest() {
            return this.postRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/upgrade', {});
        },
        saveNewApiToken(data) {
            return this.postRequest('/api/tokens', data);
        },
        sendColocationAccessRequest(data) {
            return this.postRequest('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/access/request', data);
        },
        authenticatorSetup(data) {
            return this.postRequest('/login/two-factor/authenticator/setup', data);
        },
        getGravatar(data) {
            return this.postRequest('/api/gravatar', data);
        },
        createAccountContact(data) {
            return this.postRequest('/api/contacts', data);
        },
        requestTwoFactorCode(data) {
            return this.postRequest('/api/accounts/twofactor/setup/request', data);
        },
        sendTwoFactorResponse(data) {
            return this.postRequest('/api/accounts/twofactor/setup/response', data);
        },

        /* Order requests */
        getDomainPricing() {
            return this.getRequest('/api/domains/pricing');
        },
        createOrder() {
            return this.syncPostRequest('/api/orders');
        },
        addOrderItem(orderSlug, productType) {
            return this.syncPostRequest(`/api/orders/${orderSlug}/${productType}`);
        },
        addOrderItemNoSync(orderSlug, productType) {
            return this.postRequest(`/api/orders/${orderSlug}/${productType}`);
        },
        updateOrderItem(orderSlug, productType, itemSlug, data) {
            return this.patchRequest(`/api/orders/${orderSlug}/${productType}/${itemSlug}`, data);
            return this.syncPatchRequest(`/api/orders/${orderSlug}/${productType}/${itemSlug}`, data);
        },
        updateOrderItemNoSync(orderSlug, productType, itemSlug, data) {
            return this.patchRequest(`/api/orders/${orderSlug}/${productType}/${itemSlug}`, data);
        },
        updateDediAddons(orderSlug, productType, itemSlug, data) {
            return this.syncPatchRequest(`/api/orders/${orderSlug}/${productType}/${itemSlug}/addons`, data);
        },
        updateDediLicences(orderSlug, productType, itemSlug, data) {
            return this.syncPatchRequest(`/api/orders/${orderSlug}/${productType}/${itemSlug}/licences`, data);
        },
        removeItemFromOrder(orderSlug, productType, itemSlug) {
            return this.syncDeleteRequest(`/api/orders/${orderSlug}/${productType}/${itemSlug}`);
        },
        getOrderSummary(slug) {
            return this.syncGetRequest(`/api/orders/${slug}/summary`);
        },
        isOrderLoaded() {
            return window.store.orders.loaded;
        },
        checkDomainAvailability(data) {
            return this.postRequest('/api/domains/check-availability', data);
        },
        checkIfDomainCanBeTransferred(data) {
            return this.postRequest('/api/domains/check-availability', data);
        },
        getOrderConfigOptions() {
            return this.getRequest('/api/orders/info');
        },
        getReadyToGoServers() {
            return this.getRequest('/api/orders/fetch-available-dedis');
        },
        getVpsProducts() {
            return this.getRequest('/api/orders/fetch-vps');
        },
        getMegaBundleProducts() {
            return this.getRequest('/api/orders/fetch-mega-bundles');
        },

        /** PUT REQUESTS */
        updateDomainContacts(data) {
            return this.putRequest('/api/domains/' + this.getServiceId() + '/contacts', data);
        },
        requestDomainAccessPassword() {
            return this.putRequest('/api/domains/' + this.getServiceId() + '/password', {});
        },
        updateDomainNameservers(data) {
            return this.putRequest('/api/domains/' + this.getServiceId() + '/nameservers', data);
        },
        updateDomainLockStatus() {
            return this.putRequest('/api/domains/' + this.getServiceId() + '/updatelockstatus', {});
        },
        updateAutoRenewalStatus() {
            return this.putRequest('/api/domains/' + this.getServiceId() + '/updateautorenewalstatus', {});
        },
        updateApiToken(data) {
            return this.putRequest('/api/tokens/' + data.id, data);
        },
        updateAccountContact(url, data) {
            return this.putRequest(url, data);
        },
        saveUserFeatures(url, data) {
            return this.putRequest(url, data);
        },
        updateAccountPin(data) {
            return this.putRequest('/api/clients/' + this.userDetails.whmcs_client_id + '/account-pin/update', data);
        },
        updateAccountPassword(data) {
            return this.putRequest('/api/users/' + this.userDetails.id + '/password', data);
        },
        updateAccountTwoFactor(data) {
            return this.putRequest('/api/users/' + this.userDetails.id + '/twofactor', data);
        },
        updateUserEmail(data) {
            return this.putRequest('/api/users/' + this.userDetails.id + '/email', data);
        },
        /** DELETE REQUESTS */
        deleteCreditCard() {
            return this.deleteRequest('/api/clients/' + this.userDetails.whmcs_client_id + '/credit-card/delete');
        },
        deleteApiToken(id) {
            return this.deleteRequest('/api/tokens/' + id);
        },
        removeContact(contactId) {
            return this.deleteRequest('/api/contacts/' + contactId);
        },
        setPaymentMethod(method) {
            //Store the selected payment method as preferred in userfeatures
            let data = {
                key: 'selectedPaymentMethod',
                value: method,
                user_id: this.userDetails.id,
            };
            this.postRequest('/api/user-feature/update', data).then(response => {
                if (response.data.status == 'success') {
                    this.$store.commit('setIndividualUserFeature', [data.key, data.value]);
                }
            });
            this.$store.commit('paymentMethodSelected', method);
        },
        /** @param {string} feature */
        hideInfo(feature, id) {
            let data = {
                key: feature,
                value: 'hide',
                user_id: id ? id : this.userDetails.id,
            };
            return this.postRequest('/api/user-feature/update', data).then(response => {
                if (response.data.status == 'success') {
                    /** Add to user features on successful response */
                    this.$store.commit('setIndividualUserFeature', [data.key, data.value]);
                }
            });
        },
        destroyCreditCard() {
            this.$swal({
                title: 'Are you sure?',
                text: "This will remove your credit card and will stop any future payments",
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, remove it',
                confirmButtonClass: 'btn btn-danger',
                cancelButtonText: 'No, cancel',
                cancelButtonClass: 'btn btn-link',
                buttonsStyling: false,
                reverseButtons: true,
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    return new Promise((resolve) => {
                        setTimeout(() => {
                            this.deleteCreditCard().then(response => {
                                if (response.data.status === 'success') {
                                    /** Clear all fields */

                                    this.$store.commit('setCreditCardLastFour', '');
                                    this.$store.commit('setCreditCardImage', '/img/cc-blank-aa.gif');
                                    this.$store.commit('setCreditCardType', '');
                                    this.$store.commit('setCreditCardExp', null);

                                    // Check if it is the selected payment method - if it is then reset the value
                                    if (this.paymentMethodSelected == 'creditcard') {
                                        this.$store.commit('paymentMethodSelected', '');
                                    }

                                } else {
                                    this.$swal({
                                        type: 'error',
                                        title: 'There seems to be a problem',
                                        html: 'There has been a problem removing your Credit Card. Please try again.',
                                        buttonsStyling: false,
                                        confirmButtonText: 'Close',
                                        confirmButtonClass: 'btn btn-danger'
                                    });
                                }
                            })
                                .catch(error => {
                                    this.$swal({
                                        type: 'error',
                                        title: 'There seems to be a problem',
                                        html: 'There has been a problem removing your Credit Card. Please try again.',
                                        buttonsStyling: false,
                                        confirmButtonText: 'Close',
                                        confirmButtonClass: 'btn btn-danger'
                                    });
                                })
                            resolve()
                        }, 2000)
                    })
                },
                allowOutsideClick: () => !this.$swal.isLoading()
            })
        },
        openChat() {
            if (window.hubspot_live_messages_running && window.hubspot && window.hubspot.messages) {
                window.hubspot.messages.EXPERIMENTAL_API.requestWidgetOpen();
            }
        },

        authMonitor(repollTime = 10000) {

            window.setTimeout(() => {
                axios.get('/api/auth/ping', {
                    headers: {
                        'Accept': 'application/json'
                    }

                }).then(response => {
                    this.isUserLoggedIn = response.data;

                    if(response.data === false) {
                        this.notificationUnauthenticated()
                        window.bus.$emit('notifications.unauthenticated');

                    } else {
                        this.authMonitor();
                    }

                }).catch(() => { // halt recursion
                    this.isUserLoggedIn = false;
                    this.notificationUnauthenticated()
                    window.bus.$emit('notifications.unauthenticated');
                });

            }, repollTime);
        },

        notificationUnauthenticated() {
            $('.notification-banner').removeClass('inactive');
            $('.notification-banner').removeClass('d-none');
            $('#app-ordering, #app, .main-col-l').css('top', '70px');
        },

        getMatchedProduct(product) {
            const prods = _.flatten(_.map(this.productCategories, 'products'));
            return _.find(prods, { 
                slug: (product.type) 
            });
        },
    }
}
